import { useEffect, useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useFormContext,
  useController,
} from "react-hook-form";

import { ICreateMarathon } from "features/createEditMarathon/model/types";
import { ErrorMessage } from "shared/ui/ErrorMessage/ErrorMessage";
import { buttonFileStyle } from "shared/lib/style";

import style from "./loadFile.module.scss";
import fileIcon from "assets/images/myDocument/file.svg";
import btnIcon from "assets/images/myDocument/btn_icon.svg";
import fileImg from "assets/images/admin/file.svg";
import trashRedImg from "assets/images/admin/trash_red.svg";
import { getFileName, getFileSize } from "shared/services/getFileNameAndSize";
import { toast } from "react-toastify";

type Props = {
  data: ICreateMarathon;
  languageId: number;
};

export const LoadPublicOffer: React.FC<Props> = ({ data, languageId }) => {
  const { t } = useTranslation();
  const [isDragOpen, setIsDragOpen] = useState(false);
  const [fileError, setFileError] = useState<string>("");

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<ICreateMarathon>();

  const idx = useMemo(
    () => data.publicOffer.findIndex((i) => i.languageId === languageId),
    [data.publicOffer, languageId]
  );

  const path = `publicOffer.${idx}.file` as const;
  const isUpdatedPath = `publicOffer.${idx}.isUpdated` as const;

  const { field } = useController<ICreateMarathon>({ name: path, control });
  const fileValue = field.value as File | string | null | FileList | undefined;

  const serverValue = data.publicOffer[idx]?.file ?? null;
  useEffect(() => {
    if (!fileValue && serverValue) {
      field.onChange(serverValue);
    }
  }, [serverValue]);

  if (idx < 0) return null;

  const noFileSelected =
    !fileValue ||
    (typeof FileList !== "undefined" &&
      fileValue instanceof FileList &&
      fileValue.length === 0);

  const validateFile = (file: File): string | null => {
    const allowedExt = ["pdf", "docx"];
    const ext = file.name.split(".").pop()?.toLowerCase();

    if (!ext || !allowedExt.includes(ext)) {
      return "Разрешены только файлы формата PDF и DOCX";
    }

    if (file.size > 20 * 1024 * 1024) {
      return "Размер файла не должен превышать 20 МБ";
    }

    return null;
  };

  const handleFileChange = (file: File) => {
    const error = validateFile(file);
    setFileError(error || "");

    if (!error) {
      field.onChange(file);
      setValue(isUpdatedPath, true, { shouldDirty: true, shouldValidate: true });
      toast.success("Файл успешно загружен");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.currentTarget.files?.[0] ?? null;
    if (f) {
      handleFileChange(f);
    }
    e.currentTarget.value = "";
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0] ?? null;
    if (f) {
      handleFileChange(f);
    }
    setIsDragOpen(false);
  };

  const removeFile = () => {
    field.onChange(null);
    setValue(isUpdatedPath, false, { shouldDirty: true, shouldValidate: true });
    setFileError("");
  };

  const publicError =
    (errors?.publicOffer?.[idx] as
      | { file?: { message?: string } }
      | undefined)?.file;

  return (
    <Box>
      <label className={`label-personalCabinet ${style.label}`}>
        {t("createMarathon.rules")}

        {noFileSelected ? (
          <div
            className={`${style.box_load} 
    ${isDragOpen ? style.box_load_drag : ""} 
    ${fileError || publicError?.message ? style.box_load_error : ""}`}
            onDragEnter={(e) => {
              e.preventDefault();
              setIsDragOpen(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOpen(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragOpen(false);
            }}
            onDrop={handleDrop}
          >
            <img src={fileIcon} alt="Файл" />
            <div className={style.box_load_text}>
              {isDragOpen ? (
                <p>{t("createMarathon.dropToLoad")}</p>
              ) : (
                <>
                  <p>
                    {t("createMarathon.fileDrag1")} <br />
                    {t("createMarathon.fileDrag2")}
                  </p>

                  <Button
                    variant="contained"
                    component="label"
                    sx={buttonFileStyle}
                    className={style.box_load_btn}
                  >
                    <img src={btnIcon} alt={t("createMarathon.chooseFile")} />
                    <span>{t("createMarathon.chooseFile")}</span>

                    <input
                      type="file"
                      accept=".pdf,.docx"
                      onChange={handleInputChange}
                      hidden
                    />
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className={style.main}>
            <div className={style.main__first}>
              <img className={style.main__image} src={fileImg} alt="Файл" />
              <p className={style.main__fileName}>
                {getFileName(fileValue)}
                {getFileSize(fileValue) && (
                  <span className={style.fileSize}>
                    {" "}
                    {getFileSize(fileValue)}
                  </span>
                )}
              </p>
            </div>
            <div>
              <img
                style={{ cursor: "pointer" }}
                src={trashRedImg}
                alt={t("createMarathon.deleteFile") as string}
                onClick={removeFile}
              />
            </div>
          </div>
        )}
      </label>

      <ErrorMessage error={fileError || publicError?.message} />
    </Box>
  );
};

